import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { auth } from '@/api/endpoints'
import { onSessionRefreshed, onUnauthenticated, refreshSession } from '@/api/client'
import { clearAccessToken, millisUntilExpiry, setAccessToken } from '@/api/tokenStore'
import { ApiError } from '@/api/errors'
import { queryKeys } from '@/api/queryKeys'
import type { MeResponse } from '@/api/types'
import { clearAllLocalData } from '@/features/visit/drafts'
import { platform } from '@/platform'

interface SessionApi {
  user: MeResponse | null
  loading: boolean
  signIn: (input: { email: string; password: string }) => Promise<void>
  /** Resolves to false when the server was not reached: local data is gone
   *  either way, but the session may still be live elsewhere. */
  signOut: () => Promise<boolean>
  can: (permission: string) => boolean
}

/** Who this device was last signed in as, so a change of hands is detectable. */
const LAST_USER_KEY = 'session:last-user'

/** Renew this long before the access token dies, so a request never races it. */
const RENEW_HEADROOM_MS = 60_000

const SessionContext = createContext<SessionApi | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  const me = useQuery<MeResponse, ApiError>({
    queryKey: queryKeys.me,
    queryFn: ({ signal }) => auth.me(signal),
    // Never retry: a 401 here is a definitive answer, and retrying it three
    // times just delays the sign-in screen.
    retry: false,
    staleTime: 30_000,
  })

  /** When the server says the session is gone, the cached patient data goes
   *  with it: a shared clinic desktop must not keep a half-signed-in cache. */
  useEffect(
    () =>
      onUnauthenticated(() => {
        // Cached server data goes: it belongs to a session that is over, and a
        // shared clinic desktop must not keep a half-signed-in view of a
        // patient. Everything EXCEPT the session query itself — clearing that
        // one resets the query whose 401 triggered this, which refetches, 401s
        // again, and loops forever behind a spinner that never resolves.
        queryClient.removeQueries({
          predicate: (query) => query.queryKey[0] !== queryKeys.me[0],
        })
        // Drafts do NOT go. A 401 here usually means the refresh token expired
        // while the tab slept, or the connection dropped mid-renewal — and
        // throwing away a half-typed consultation for either is the one failure
        // this application is built to prevent. They are cleared on a
        // deliberate sign-out, and by the check in signIn below if somebody
        // else sits down at this machine.
      }),
    [queryClient],
  )

  const signInMutation = useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      auth.signIn({ email: input.email, password: input.password }),
    onSuccess: async (session) => {
      setAccessToken(session.access_token, session.expires_in)
      // The desktop changed hands: nothing the last person left behind stays on
      // it, including a draft that a lock deliberately preserved.
      const previous = await platform.storage.get<string>(LAST_USER_KEY)
      if (previous && previous !== session.user.id) await clearAllLocalData()
      await platform.storage.set(LAST_USER_KEY, session.user.id)
      queryClient.setQueryData(queryKeys.me, session.user)
    },
  })

  const signOutMutation = useMutation({
    mutationFn: () => auth.signOut(),
    onSettled: async () => {
      clearAccessToken()
      queryClient.clear()
      await clearAllLocalData()
    },
  })

  // A refresh that the API client started (replaying a 401) produces a whole
  // session, not just a token — take the user from it rather than refetching.
  useEffect(
    () =>
      onSessionRefreshed((session) => {
        queryClient.setQueryData(queryKeys.me, (session as { user: MeResponse }).user)
      }),
    [queryClient],
  )

  // Renew *before* the token dies. Waiting for a 401 works — the client replays
  // the request — but it spends a failed round trip first, and a screen opened
  // after a long idle would fire a burst of them at once.
  const expiresIn = me.data ? millisUntilExpiry() : 0
  useEffect(() => {
    if (!me.data) return
    const delay = Math.max(5_000, millisUntilExpiry() - RENEW_HEADROOM_MS)
    const timer = setTimeout(() => void refreshSession(), delay)
    return () => clearTimeout(timer)
  }, [me.data, expiresIn])

  // A laptop that was asleep comes back with a token that expired hours ago and
  // no timer that ever fired, so the first click would 401.
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState !== 'visible') return
      if (me.data && millisUntilExpiry() < RENEW_HEADROOM_MS) void refreshSession()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [me.data])

  // Read outside the memo: inside a `isLoading && …` expression TanStack's
  // discriminated result narrows `error` to null, which makes the check
  // unreachable rather than merely false.
  const signedOut = me.error?.isUnauthenticated ?? false
  const stillLoading = me.isPending && !signedOut

  const value = useMemo<SessionApi>(() => {
    const user = me.data ?? null
    return {
      user,
      // A 401 is a definitive "not signed in", not a loading state.
      loading: stillLoading,
      signIn: async (input) => {
        await signInMutation.mutateAsync(input)
      },
      signOut: async () => {
        try {
          await signOutMutation.mutateAsync()
          return true
        } catch {
          // The local cache and drafts are cleared in onSettled regardless, so
          // the device is safe to hand over. The server session may not be, and
          // the caller is told so rather than being shown a silent success.
          return false
        }
      },
      can: (permission) => user?.permissions.includes(permission) ?? false,
    }
  }, [me.data, stillLoading, signInMutation, signOutMutation])

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionApi {
  const api = useContext(SessionContext)
  if (!api) throw new Error('useSession must be used inside SessionProvider')
  return api
}
